/**
 * CameraBurst — full-screen in-app camera.
 *  - Rapid multi-photo capture (burst) with thumbnails strip.
 *  - Pinch-to-zoom + slider: uses the phone's real lens zoom where the browser
 *    exposes it (Android Chrome), falls back to digital zoom (preview scale +
 *    centre-crop on capture) everywhere else, incl. iOS Safari.
 *  - Video mode: record clips up to 60s, review, then chunk-upload against the
 *    checklist item for later playback.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Camera, X, Check, RotateCcw, Trash2, AlertCircle, ImageOff, Video, Square, Loader2, ZoomIn } from "lucide-react";
import { useOverlayBackClose } from "@/hooks/backGuard";
import { uploadVideo, fmtClipDuration } from "@/lib/videoUpload";
import { stampTimestamp } from "@/lib/checklist";
import { toast } from "sonner";

const JPEG_QUALITY = 0.82;   // ~80KB per 4:3 phone-camera frame
const MAX_LONG_EDGE = 1600;  // downscale big frames so uploads stay quick
const MAX_VIDEO_SECONDS = 60;

const pickVideoMime = () => {
  if (typeof MediaRecorder === "undefined") return "";
  return ["video/mp4", "video/webm;codecs=vp9", "video/webm;codecs=vp8", "video/webm"]
    .find((c) => MediaRecorder.isTypeSupported(c)) || "";
};

const touchDist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);

const CameraBurst = ({ open, onClose, onDone, remaining = 10, inspectionId, itemId, onVideoSaved }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const zoomRef = useRef(null);
  const pinchRef = useRef(null);
  const recorderRef = useRef(null);
  const recChunksRef = useRef([]);
  const timerRef = useRef(null);
  const elapsedRef = useRef(0);

  const [shots, setShots] = useState([]);      // [{ id, url }]
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const [facingMode, setFacingMode] = useState("environment");
  const [flashing, setFlashing] = useState(false);
  const [zoom, setZoom] = useState(null);      // { kind, min, max, step, value }
  const [mode, setMode] = useState("photo");   // "photo" | "video"
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [reviewClip, setReviewClip] = useState(null); // { blob, url, mime, duration }
  const [uploading, setUploading] = useState(null);   // null | 0-100

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const startStream = useCallback(async (facing, withAudio) => {
    stopStream();
    setError(null);
    setReady(false);
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error("Your browser does not support the in-app camera. Use the gallery picker instead.");
      }
      const videoC = { facingMode: facing, width: { ideal: 1920 }, height: { ideal: 1440 } };
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: withAudio, video: videoC });
      } catch (err) {
        if (!withAudio) throw err;
        // Mic denied — still record, just silent
        stream = await navigator.mediaDevices.getUserMedia({ audio: false, video: videoC });
      }
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.style.transform = "none";
        await videoRef.current.play().catch(() => {});
        setReady(true);
      }
      // Zoom capability detection — native lens zoom where the browser exposes it
      const track = stream.getVideoTracks()[0];
      const caps = track?.getCapabilities?.() || {};
      const z = caps.zoom
        ? { kind: "native", min: caps.zoom.min ?? 1, max: caps.zoom.max ?? 8, step: caps.zoom.step || 0.1, value: track.getSettings?.().zoom ?? caps.zoom.min ?? 1 }
        : { kind: "digital", min: 1, max: 5, step: 0.1, value: 1 };
      zoomRef.current = z;
      setZoom(z);
    } catch (e) {
      setError(
        e?.name === "NotAllowedError"
          ? "Camera permission was denied. Enable it in your browser settings and try again."
          : e?.message || "Could not open the camera."
      );
      setReady(false);
    }
  }, [stopStream]);

  useEffect(() => {
    if (!open) {
      stopStream();
      setShots([]); setReady(false); setMode("photo");
      setRecording(false); setReviewClip(null); setUploading(null);
      clearInterval(timerRef.current);
      return;
    }
    startStream(facingMode, mode === "video");
    return () => { stopStream(); clearInterval(timerRef.current); };
  }, [open, facingMode, mode, startStream, stopStream]);

  // ---------- zoom ----------
  const setZoomValue = (v) => {
    const z = zoomRef.current;
    if (!z) return;
    const value = Math.min(z.max, Math.max(z.min, v));
    const next = { ...z, value };
    zoomRef.current = next;
    setZoom(next);
    if (z.kind === "native") {
      streamRef.current?.getVideoTracks()[0]
        ?.applyConstraints({ advanced: [{ zoom: value }] }).catch(() => {});
    } else if (videoRef.current) {
      videoRef.current.style.transform = value > 1.01 ? `scale(${value})` : "none";
    }
  };

  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      pinchRef.current = { start: touchDist(e.touches), base: zoomRef.current?.value || 1 };
    }
  };
  const onTouchMove = (e) => {
    if (e.touches.length === 2 && pinchRef.current) {
      setZoomValue(pinchRef.current.base * (touchDist(e.touches) / pinchRef.current.start));
    }
  };
  const onTouchEnd = () => { pinchRef.current = null; };

  // ---------- photo ----------
  const snap = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !ready) return;
    if (shots.length >= remaining) return;

    const vw = video.videoWidth || 1280;
    const vh = video.videoHeight || 960;
    // Digital zoom: capture the centre-cropped region the user sees
    const z = zoomRef.current;
    let sx = 0, sy = 0, sw = vw, sh = vh;
    if (z && z.kind === "digital" && z.value > 1.01) {
      sw = vw / z.value; sh = vh / z.value;
      sx = (vw - sw) / 2; sy = (vh - sh) / 2;
    }
    const scale = Math.min(1, MAX_LONG_EDGE / Math.max(sw, sh));
    canvas.width = Math.round(sw * scale);
    canvas.height = Math.round(sh * scale);
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
    stampTimestamp(ctx, canvas.width, canvas.height);
    const url = canvas.toDataURL("image/jpeg", JPEG_QUALITY);
    setShots((prev) => [...prev, { id: Date.now() + Math.random(), url }]);
    setFlashing(true);
    setTimeout(() => setFlashing(false), 120);
    try { navigator.vibrate?.(20); } catch { /* not supported */ }
  };

  const remove = (id) => setShots((prev) => prev.filter((s) => s.id !== id));
  const flipCamera = () => setFacingMode((m) => (m === "environment" ? "user" : "environment"));

  const finish = () => {
    if (shots.length === 0) return;
    onDone(shots.map((s) => s.url));
  };

  // ---------- video ----------
  const stopRecording = () => {
    try {
      if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop();
    } catch { /* already stopped */ }
  };

  const beginRecording = () => {
    const stream = streamRef.current;
    if (!stream || !ready) return;
    const mime = pickVideoMime();
    let rec;
    try {
      rec = new MediaRecorder(stream, mime ? { mimeType: mime, videoBitsPerSecond: 2_500_000 } : undefined);
    } catch {
      toast.error("Video recording is not supported on this browser");
      return;
    }
    recChunksRef.current = [];
    rec.ondataavailable = (e) => { if (e.data && e.data.size) recChunksRef.current.push(e.data); };
    rec.onstop = () => {
      clearInterval(timerRef.current);
      const type = rec.mimeType || mime || "video/webm";
      const blob = new Blob(recChunksRef.current, { type });
      if (blob.size > 0) {
        setReviewClip({ blob, url: URL.createObjectURL(blob), mime: type, duration: elapsedRef.current });
      }
      setRecording(false);
    };
    recorderRef.current = rec;
    elapsedRef.current = 0;
    setElapsed(0);
    rec.start(1000);
    setRecording(true);
    try { navigator.vibrate?.(30); } catch { /* not supported */ }
    timerRef.current = setInterval(() => {
      elapsedRef.current += 1;
      setElapsed(elapsedRef.current);
      if (elapsedRef.current >= MAX_VIDEO_SECONDS) stopRecording();
    }, 1000);
  };

  const discardClip = () => {
    if (reviewClip) URL.revokeObjectURL(reviewClip.url);
    setReviewClip(null);
    setUploading(null);
  };

  const saveClip = async () => {
    if (!reviewClip || !inspectionId) return;
    setUploading(0);
    try {
      await uploadVideo({
        inspectionId, itemId,
        blob: reviewClip.blob, mime: reviewClip.mime, duration: reviewClip.duration,
        onProgress: setUploading,
      });
      toast.success("Video saved — find it under this question anytime");
      URL.revokeObjectURL(reviewClip.url);
      setReviewClip(null);
      onVideoSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Video upload failed — check your connection and tap Save again");
    } finally {
      setUploading(null);
    }
  };

  // Phone back button while the camera is open: don't leave the checklist.
  useOverlayBackClose(open, () => {
    if (recording) { stopRecording(); return; }
    if (reviewClip) { discardClip(); return; }
    if (shots.length > 0) {
      if (window.confirm(`Attach the ${shots.length} photo${shots.length > 1 ? "s" : ""} you just took to this question?`)) {
        onDone(shots.map((s) => s.url));
        return;
      }
    }
    onClose();
  });

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9999] bg-black flex flex-col" data-testid="camera-burst">
      {/* Top bar */}
      <div className="shrink-0 h-16 px-3 flex items-center justify-between bg-black/70 text-white gap-2">
        <button onClick={onClose} data-testid="camera-burst-close" disabled={recording}
          className="h-11 px-3 inline-flex items-center gap-1.5 text-sm font-bold shrink-0 disabled:opacity-40">
          <X size={20} strokeWidth={2.5}/> Cancel
        </button>
        <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-center flex-1 min-w-0" data-testid="camera-burst-counter">
          {mode === "video"
            ? (recording
              ? <span className="text-red-400 inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-red-500 animate-pulse inline-block"/> {fmtClipDuration(elapsed)} / 1:00</span>
              : "Video · max 60s")
            : `${shots.length} / ${remaining} photo${remaining === 1 ? "" : "s"}`}
        </div>
        <button onClick={flipCamera} disabled={!ready || recording} data-testid="camera-burst-flip"
          title="Flip camera"
          className="h-11 w-11 inline-flex items-center justify-center disabled:opacity-40 shrink-0">
          <RotateCcw size={18} strokeWidth={2.5}/>
        </button>
        <button
          onClick={finish}
          disabled={shots.length === 0 || mode === "video"}
          data-testid="camera-burst-done"
          className="h-11 px-3.5 inline-flex items-center gap-1.5 bg-emerald-500 text-white text-sm font-black uppercase tracking-wide hover:bg-emerald-600 disabled:bg-white/10 disabled:text-white/40 shrink-0">
          <Check size={16} strokeWidth={3}/>
          Attach{shots.length ? ` ${shots.length}` : ""}
        </button>
      </div>

      {/* Camera preview — pinch to zoom */}
      <div className="flex-1 relative overflow-hidden bg-black touch-none"
        onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>
        {error ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center text-white">
            <AlertCircle size={40} className="text-red-400 mb-3"/>
            <p className="text-sm font-bold">{error}</p>
            <button onClick={() => startStream(facingMode, mode === "video")} className="mt-4 h-10 px-4 bg-white text-slate-900 font-bold text-sm">
              Try again
            </button>
          </div>
        ) : (
          <>
            <video ref={videoRef} autoPlay playsInline muted
              className={`w-full h-full object-cover ${flashing ? "opacity-20" : ""} transition-opacity`}/>
            {!ready && (
              <div className="absolute inset-0 flex items-center justify-center text-white/70 text-sm">
                Opening camera…
              </div>
            )}
            {zoom && zoom.value > zoom.min + 0.01 && (
              <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-black/60 text-white text-xs font-black px-2.5 py-1" data-testid="camera-zoom-indicator">
                {zoom.value.toFixed(1)}x
              </div>
            )}
          </>
        )}
        <canvas ref={canvasRef} className="hidden"/>
      </div>

      {/* Zoom slider */}
      {ready && !error && zoom && (
        <div className="shrink-0 bg-black px-4 py-2 flex items-center gap-3" data-testid="camera-zoom-row">
          <ZoomIn size={15} className="text-white/70 shrink-0"/>
          <input
            type="range"
            min={zoom.min} max={zoom.max} step={zoom.step} value={zoom.value}
            onChange={(e) => setZoomValue(parseFloat(e.target.value))}
            className="flex-1 accent-white h-6"
            data-testid="camera-zoom-slider"
          />
          <span className="text-white/80 text-xs font-black w-10 text-right">{zoom.value.toFixed(1)}x</span>
        </div>
      )}

      {/* Thumbnails strip */}
      {mode === "photo" && shots.length > 0 && (
        <div className="shrink-0 bg-black/80 px-3 py-2 flex gap-2 overflow-x-auto" data-testid="camera-burst-strip">
          {shots.map((s) => (
            <div key={s.id} className="relative shrink-0" data-testid={`camera-burst-thumb-${s.id}`}>
              <img src={s.url} alt="capture" className="h-16 w-16 object-cover border-2 border-white/30"/>
              <button onClick={() => remove(s.id)}
                data-testid={`camera-burst-remove-${s.id}`}
                className="absolute -top-1 -right-1 h-5 w-5 bg-red-600 text-white inline-flex items-center justify-center">
                <Trash2 size={11} strokeWidth={2.5}/>
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Shutter row — photo/video mode toggle + shutter/record */}
      <div className="shrink-0 h-28 bg-black flex items-center justify-center gap-6 pl-4 pr-28">
        {inspectionId && (
          <div className="flex flex-col gap-1.5">
            <button onClick={() => !recording && setMode("photo")} data-testid="camera-mode-photo"
              disabled={recording}
              className={`h-9 px-3 inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-wider border-2 ${mode === "photo" ? "bg-white text-slate-900 border-white" : "text-white/70 border-white/30"} disabled:opacity-40`}>
              <Camera size={13} strokeWidth={2.5}/> Photo
            </button>
            <button onClick={() => !recording && setMode("video")} data-testid="camera-mode-video"
              disabled={recording}
              className={`h-9 px-3 inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-wider border-2 ${mode === "video" ? "bg-red-600 text-white border-red-600" : "text-white/70 border-white/30"} disabled:opacity-40`}>
              <Video size={13} strokeWidth={2.5}/> Video
            </button>
          </div>
        )}
        {mode === "photo" ? (
          <button onClick={snap} disabled={!ready || !!error || shots.length >= remaining}
            data-testid="camera-burst-snap"
            className="h-20 w-20 rounded-full border-4 border-white bg-white/90 active:bg-white disabled:opacity-40 relative">
            <Camera size={28} className="absolute inset-0 m-auto text-slate-900" strokeWidth={2.5}/>
          </button>
        ) : (
          <button onClick={recording ? stopRecording : beginRecording} disabled={!ready || !!error}
            data-testid="camera-record-button"
            className={`h-20 w-20 rounded-full border-4 border-white relative disabled:opacity-40 ${recording ? "bg-red-600" : "bg-red-500/90 active:bg-red-500"}`}>
            {recording
              ? <Square size={26} className="absolute inset-0 m-auto text-white fill-white" strokeWidth={2.5}/>
              : <Video size={28} className="absolute inset-0 m-auto text-white" strokeWidth={2.5}/>}
          </button>
        )}
      </div>

      {/* Video review overlay */}
      {reviewClip && (
        <div className="absolute inset-0 z-20 bg-black flex flex-col" data-testid="video-review">
          <div className="shrink-0 h-16 px-4 flex items-center justify-between bg-black/80 text-white">
            <span className="text-sm font-black uppercase tracking-wider">Review video · {fmtClipDuration(reviewClip.duration)}</span>
            <button onClick={discardClip} disabled={uploading !== null} data-testid="video-review-close"
              className="h-11 w-11 inline-flex items-center justify-center disabled:opacity-40"><X size={20}/></button>
          </div>
          <video src={reviewClip.url} controls playsInline className="flex-1 min-h-0 w-full object-contain bg-black"/>
          <div className="shrink-0 p-4 bg-black flex gap-2 pr-28">
            <button onClick={discardClip} disabled={uploading !== null} data-testid="video-review-discard"
              className="h-12 px-4 border-2 border-white/40 text-white font-bold text-sm disabled:opacity-40">
              Discard
            </button>
            <button onClick={saveClip} disabled={uploading !== null} data-testid="video-review-save"
              className="flex-1 h-12 inline-flex items-center justify-center gap-2 bg-emerald-500 text-white font-black text-sm uppercase tracking-wider hover:bg-emerald-600 disabled:opacity-70">
              {uploading !== null ? <><Loader2 size={15} className="animate-spin"/> Uploading… {uploading}%</> : <><Check size={15} strokeWidth={3}/> Save video</>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CameraBurst;
export { ImageOff };  // convenience re-export for parents that show a fallback
